/**
 * Schémas Zod du module Stock bouteilles (Lot D) — partagés API ⇄ ERP.
 *
 * `lots_bouteilles` = population HOMOGÈNE mono-stade/mono-format/mono-emplacement
 * (cache de solde) ; `mouvements_bouteilles` = registre APPEND-ONLY, SOURCE DE
 * VÉRITÉ du stock (quantité SIGNÉE, négatif = sortie). Toute transition partielle
 * SCINDE le lot (paire de mouvements `transfert`). Correction = mouvement
 * `annulation`, jamais d'édition. Le stock s'affiche TOUJOURS ventilé par stade.
 * Source des énumérations/CHECKs : migration `0058_lot_d_cave.sql`.
 */

import { z } from "zod";

import { idSchema } from "./parcellaire.js";
import { formatClSchema, nbBouteillesSchema, NB_BOUTEILLES_MAX } from "./tirage.js";
import { dateIsoSchema } from "./vendange.js";

// ────────────────────────────────────────────────────────────────────────────
// Énumérations (miroir des CHECK SQL)
// ────────────────────────────────────────────────────────────────────────────

/** Stade d'un lot — miroir CHECK SQL `lots_bouteilles.stade`. */
export const lotStadeSchema = z.enum(["sur_lattes", "sur_pointes", "degorgee", "habillee"]);
export type LotStade = z.infer<typeof lotStadeSchema>;

/** Origine d'un lot — miroir CHECK SQL `lots_bouteilles.origine`. */
export const lotOrigineSchema = z.enum(["tirage", "reprise_cooperative"]);
export type LotOrigine = z.infer<typeof lotOrigineSchema>;

/** Statut d'un lot — miroir CHECK SQL `lots_bouteilles.statut` (annule = op. annulée). */
export const lotStatutSchema = z.enum(["actif", "annule"]);
export type LotStatut = z.infer<typeof lotStatutSchema>;

/** Couleur finale d'un lot de Champagne, héritée de l'assemblage validé. */
export const couleurLotBouteillesSchema = z.enum(["blanc", "rose"]);
export type CouleurLotBouteilles = z.infer<typeof couleurLotBouteillesSchema>;

/**
 * Type de mouvement — miroir CHECK SQL `mouvements_bouteilles.type`.
 * `transfert` = scission/regroupement/passage lattes↔pointes ; `temoin` =
 * bouteille témoin analyses ; `expedition` = SORTIE commerciale (brique minimale
 * Lot F pour la DRM/DS/CRD — cf module Expédition) ; `retour_client` = ENTRÉE de
 * réintégration d'un avoir avec retour marchandise (Lot G, DRM = réintégrations).
 * Les entrées/opérations sont créées par leurs flux (tirage, reprise, dégorgement,
 * habillage, avoir avec retour) — jamais saisies directement.
 */
export const mouvementBouteillesTypeSchema = z.enum([
  "entree_tirage",
  "entree_reprise",
  "retour_client",
  "transfert",
  "degorgement",
  "appoint_degorgement",
  "habillage",
  "casse",
  "temoin",
  "expedition",
  "ajustement",
  "annulation",
]);
export type MouvementBouteillesType = z.infer<typeof mouvementBouteillesTypeSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Ventilation par stade (anti-pattern « total unique » — D.0)
// ────────────────────────────────────────────────────────────────────────────

/** Stock ventilé par stade — JAMAIS de total unique (règle D.0). */
export const stockParStadeSchema = z.object({
  sur_lattes: z.number().int().nonnegative(),
  sur_pointes: z.number().int().nonnegative(),
  degorgee: z.number().int().nonnegative(),
  habillee: z.number().int().nonnegative(),
});
export type StockParStade = z.infer<typeof stockParStadeSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Texte libre court optionnel — vide normalisé en undefined. */
const optionalShortText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} trop long (${max} caractères max).`)
    .optional()
    .or(z.literal("").transform(() => undefined));

/** Notes libres (multi-ligne) optionnelles — vide normalisé en undefined. */
const notesSchema = z
  .string()
  .trim()
  .max(4000, "Notes trop longues (4000 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

// ────────────────────────────────────────────────────────────────────────────
// Input — lecture des lots
// ────────────────────────────────────────────────────────────────────────────

/** Input `stock.getLot` — fiche détail d'un lot + son registre. */
export const lotGetByIdInput = z.object({ id: idSchema });
export type LotGetByIdInput = z.infer<typeof lotGetByIdInput>;

/**
 * Valeur sentinelle du filtre d'emplacement : « les lots rangés nulle part ».
 *
 * Un identifiant de zone ne peut pas exprimer l'absence de zone, et un filtre
 * omis veut dire « toutes les zones ». D'où ce littéral, partagé serveur ⇄ ERP
 * pour qu'aucun des deux ne le réécrive à la main.
 */
export const SANS_EMPLACEMENT = "sans_emplacement" as const;

/** Input `stock.listerLots` — filtres optionnels (défaut serveur : statut actif). */
export const lotListerInput = z
  .object({
    stade: lotStadeSchema.optional(),
    origine: lotOrigineSchema.optional(),
    statut: lotStatutSchema.optional(),
    /**
     * Zone de rangement. Le littéral `"sans_emplacement"` cible les lots qui
     * n'en ont AUCUNE (`emplacement_id IS NULL`) — impossible à demander
     * autrement, un `emplacement_id` absent signifiant « pas de filtre ».
     * C'est ce qui permet au plan de cave de montrer ce qu'il reste à ranger.
     */
    emplacement_id: z.union([idSchema, z.literal(SANS_EMPLACEMENT)]).optional(),
    /** Filtre par gamme (jointure tirage→assemblage→cuvée OU reprise→cuvée). */
    cuvee_commerciale_id: idSchema.optional(),
    format_cl: formatClSchema.optional(),
  })
  .optional();
export type LotListerInput = z.infer<typeof lotListerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Inputs — mouvements de stock (append-only, permission `stock.mouvement`)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `stock.transfererLot` — scission / déplacement / passage lattes↔pointes.
 * Crée un NOUVEAU lot (copie format/date de tirage/millésime/généalogie) + une
 * paire de mouvements `transfert` (−quantité source, +quantité cible).
 * `nouveau_stade` absent = même stade (simple déplacement d'emplacement).
 */
export const lotTransfertStadeInput = z
  .object({
    /** Lot source du transfert. */
    lot_id: idSchema,
    /** Bouteilles transférées (≤ solde du lot — vérifié en use case). */
    quantite: nbBouteillesSchema,
    /** Nouveau stade (remuage lattes↔pointes uniquement) — absent = inchangé. */
    nouveau_stade: lotStadeSchema.optional(),
    /** Emplacement de destination (null = sans emplacement). */
    emplacement_id: idSchema.nullable().optional(),
    /** Repère physique facultatif dans le tas de destination. */
    repere_palette: optionalShortText(40, "Repère palette").nullable(),
    /** Date du mouvement. */
    date_mouvement: dateIsoSchema,
    /** Opérateur. */
    operateur_id: idSchema.optional(),
    /** Notes libres. */
    notes: notesSchema,
  })
  .superRefine((valeur, ctx) => {
    // Les stades dégorgée/habillée ne s'atteignent QUE par leur opération
    // (dégorgement/habillage) — un transfert ne peut pas les poser.
    if (valeur.nouveau_stade === "degorgee" || valeur.nouveau_stade === "habillee") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["nouveau_stade"],
        message: "Ce stade s'atteint uniquement par l'opération dédiée (dégorgement / habillage).",
      });
    }
  });
export type LotTransfertStadeInput = z.infer<typeof lotTransfertStadeInput>;

/** Input `stock.declarerCasse` — casse (quantité saisie POSITIVE, stockée négative). */
export const lotCasseInput = z.object({
  lot_id: idSchema,
  /** Bouteilles cassées. */
  quantite: nbBouteillesSchema,
  date_mouvement: dateIsoSchema,
  operateur_id: idSchema.optional(),
  /** Motif libre (chute de palette, verre défectueux…). */
  motif: optionalShortText(120, "Motif"),
});
export type LotCasseInput = z.infer<typeof lotCasseInput>;

/**
 * Input `stock.prelevementTemoin` — SORTIE NON COMMERCIALE de bouteille(s) (défaut 1).
 * Couvre le témoin d'analyse ET les autres sorties non vendues (dégustation client,
 * offert, échantillon, presse) : même rubrique DRM `consommation_degustation`
 * (cadrage §5). Le `motif` précise le cas ; le libellé registre est élargi en
 * conséquence (cf `registre-libelles.ts`).
 */
export const lotTemoinInput = z.object({
  lot_id: idSchema,
  /** Bouteilles prélevées (saisie positive, stockée négative). */
  quantite: nbBouteillesSchema.default(1),
  date_mouvement: dateIsoSchema,
  operateur_id: idSchema.optional(),
  /** Motif libre (témoin/analyses, dégustation, offert, échantillon, presse…). */
  motif: optionalShortText(120, "Motif"),
});
export type LotTemoinInput = z.infer<typeof lotTemoinInput>;

/** Input `stock.ajusterLot` — écart d'inventaire, quantité SIGNÉE non nulle. */
export const lotAjustementInput = z.object({
  lot_id: idSchema,
  /** Écart signé : positif = entrée correctrice, négatif = sortie. */
  quantite: z
    .number({ invalid_type_error: "Quantité invalide.", required_error: "Quantité requise." })
    .int("La quantité doit être un nombre entier de bouteilles.")
    .min(-NB_BOUTEILLES_MAX, `Quantité minimum : -${NB_BOUTEILLES_MAX}.`)
    .max(NB_BOUTEILLES_MAX, `Quantité maximum : ${NB_BOUTEILLES_MAX}.`)
    .refine((valeur) => valeur !== 0, "La quantité d'un ajustement ne peut être nulle."),
  date_mouvement: dateIsoSchema,
  operateur_id: idSchema.optional(),
  /** Motif libre (inventaire annuel, erreur de comptage…). */
  motif: optionalShortText(120, "Motif"),
});
export type LotAjustementInput = z.infer<typeof lotAjustementInput>;

/**
 * Input `stock.annulerMouvement` — annulation par OPÉRATION INVERSE : mouvement
 * `annulation` portant la quantité opposée et pointant l'original
 * (`mouvement_annule_id`, anti-double-annulation via index 0059).
 */
export const annulerMouvementBouteillesInput = z.object({
  /** Mouvement à annuler. */
  mouvement_id: idSchema,
  /** Date de l'annulation (défaut : aujourd'hui côté serveur). */
  date_mouvement: dateIsoSchema.optional(),
  /** Motif libre de l'annulation (tracé). */
  motif: optionalShortText(120, "Motif"),
});
export type AnnulerMouvementBouteillesInput = z.infer<typeof annulerMouvementBouteillesInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Représentation d'un lot de bouteilles (LotSortie). Les échéances AOC sont
 * calculées serveur depuis `date_tirage_reference` via `tirage-calculs.ts` ;
 * `numero_lot_reference` / `emplacement_nom` sont des enrichissements de lecture.
 */
export const lotBouteillesSortieSchema = z.object({
  id: idSchema,
  libelle: z.string().nullable(),
  reference_interne: z.string().nullable(),
  origine: lotOrigineSchema,
  tirage_id: idSchema.nullable(),
  reprise_id: idSchema.nullable(),
  degorgement_id: idSchema.nullable(),
  habillage_id: idSchema.nullable(),
  stade: lotStadeSchema,
  format_cl: z.string(),
  nb_bouteilles: z.number().int().nonnegative(),
  /** Date de tirage propre OU coopérative — porte les délais 12/15/36 mois. */
  date_tirage_reference: z.string(),
  millesime: z.number().int().nullable(),
  emplacement_id: idSchema.nullable(),
  repere_palette: z.string().nullable(),
  statut: lotStatutSchema,
  notes: z.string().nullable(),
  cree_le: z.date(),
  /** N° de lot d'origine affichable (tirage maison ou n° lot coop). */
  numero_lot_reference: z.string().nullable(),
  /**
   * Nom de la coopérative d'origine (lots repris, null pour un tirage maison) —
   * libellé de repli UI quand la reprise n'a pas de n° de lot coop.
   */
  cooperative_nom: z.string().nullable(),
  /** Nom de l'emplacement (plan de cave) pour affichage direct. */
  emplacement_nom: z.string().nullable(),
  /** Couleur héritée de l'assemblage ; null pour un historique non qualifié. */
  couleur_finale: couleurLotBouteillesSchema.nullable(),
  /** Cuvée commerciale résolue par l'assemblage ou la reprise coopérative. */
  cuvee_commerciale_id: idSchema.nullable(),
  cuvee_nom: z.string().nullable(),
  /** Assemblage parent du tirage maison ; null pour une reprise coopérative. */
  assemblage_id: idSchema.nullable(),
  assemblage_reference: z.string().nullable(),
  /** Vrai si le lot est millésimé (délai de mise en marché 36 mois). */
  est_millesime: z.boolean(),
  /** Première date de dégorgement autorisée (référence + 12 mois). */
  date_degorgement_mini: dateIsoSchema.nullable(),
  /** Première date de mise en marché consommateur (référence + 15/36 mois). */
  date_mise_en_marche_mini: dateIsoSchema.nullable(),
});
export type LotBouteillesSortie = z.infer<typeof lotBouteillesSortieSchema>;

/** Sortie `stock.listerLots` — liste de lots. */
export const lotListeSortieSchema = z.array(lotBouteillesSortieSchema);

/** Représentation d'un mouvement du registre bouteilles (quantité SIGNÉE). */
export const mouvementBouteillesSortieSchema = z.object({
  id: idSchema,
  lot_bouteilles_id: idSchema,
  type: mouvementBouteillesTypeSchema,
  quantite: z.number().int(),
  lot_contrepartie_id: idSchema.nullable(),
  degorgement_id: idSchema.nullable(),
  habillage_id: idSchema.nullable(),
  mouvement_annule_id: idSchema.nullable(),
  date_mouvement: z.string(),
  operateur_id: idSchema.nullable(),
  notes: z.string().nullable(),
  cree_le: z.date(),
});
export type MouvementBouteillesSortie = z.infer<typeof mouvementBouteillesSortieSchema>;

/** Sortie `stock.getLot` — fiche détail + registre des mouvements. */
export const lotDetailSortieSchema = lotBouteillesSortieSchema.extend({
  mouvements: z.array(mouvementBouteillesSortieSchema),
  /** Contrôle à la date réelle du dégorgement, distinct des règles du jour. */
  controle_degorgement_historique: z
    .object({
      date_degorgement: dateIsoSchema,
      date_degorgement_mini: dateIsoSchema.nullable(),
      regle_id: idSchema.nullable(),
    })
    .nullable(),
});
export type LotDetailSortie = z.infer<typeof lotDetailSortieSchema>;

/** Sortie casse/témoin/ajustement — mouvement créé + nouveau solde du lot. */
export const mouvementBouteillesCreerSortieSchema = z.object({
  mouvement_id: idSchema,
  lot_bouteilles_id: idSchema,
  /** Nouveau solde (cache) après le mouvement. */
  nb_bouteilles: z.number().int().nonnegative(),
  cree_le: z.date(),
});
export type MouvementBouteillesCreerSortie = z.infer<typeof mouvementBouteillesCreerSortieSchema>;

/** Sortie `stock.transfererLot` — paire de mouvements + lots source/cible. */
export const lotTransfertSortieSchema = z.object({
  mouvement_sortie_id: idSchema,
  mouvement_entree_id: idSchema,
  lot_source_id: idSchema,
  lot_source_nb_bouteilles: z.number().int().nonnegative(),
  /** Nouveau lot créé par la scission (ou lot cible du regroupement). */
  lot_cible_id: idSchema,
  lot_cible_nb_bouteilles: z.number().int().nonnegative(),
});
export type LotTransfertSortie = z.infer<typeof lotTransfertSortieSchema>;

/** Sortie `stock.annulerMouvement` — mouvement inverse + nouveau solde. */
export const annulerMouvementBouteillesSortieSchema = z.object({
  mouvement_id: idSchema,
  mouvement_annule_id: idSchema,
  lot_bouteilles_id: idSchema,
  nb_bouteilles: z.number().int().nonnegative(),
});
export type AnnulerMouvementBouteillesSortie = z.infer<
  typeof annulerMouvementBouteillesSortieSchema
>;
